import type { Action } from '../types/game';
import type { GameState } from '../engine/GameState';
import { addUnique } from '../engine/GameState';

/**
 * Efecto derivado de ejecutar una acción.
 *
 * El intérprete muta el estado para los cambios de datos (variables, items,
 * evidencias, objetivos, escenarios visitados) y devuelve efectos para que el
 * motor orqueste lo que depende del flujo (cambios de escena, diálogos,
 * mensajes, final). Así el intérprete es headless y testeable.
 */
export type ActionEffect =
  | { kind: 'scene'; scene: string }
  | { kind: 'dialogue'; dialogue: string; node?: string }
  | { kind: 'message'; text: string; tone: 'info' | 'success' | 'warning' | 'hint' }
  | { kind: 'finish'; ending: string }
  | { kind: 'conclusion' }
  | { kind: 'itemAdded'; item: string }
  | { kind: 'itemRemoved'; item: string }
  | { kind: 'evidenceAdded'; evidence: string }
  | { kind: 'objectiveCompleted'; objective: string }
  | { kind: 'variableSet'; var: string; value: string | number | boolean }
  | { kind: 'cameraZoom'; target?: { x: number; y: number }; scale: number; duration: number; hold: number }
  | { kind: 'cameraPan'; to: { x: number; y: number }; duration: number; hold: number }
  | { kind: 'objectStateChanged'; object: string; state: string; sfx?: string }
  | { kind: 'sequence'; sequence: string }
  | { kind: 'encounter'; encounter: string }
  | { kind: 'minigame'; minigame: string }
  | { kind: 'content'; content: string }
  | {
      kind: 'video';
      video: string;
      titulo?: string;
      descripcion?: string;
      modo: 'modal' | 'tv';
      objeto?: string;
      ancho: number;
      poster?: string;
    };

/** Error lanzado ante una acción desconocida (vocabulario cerrado). */
export class UnknownActionError extends Error {
  constructor(public readonly action: unknown) {
    super(
      `Acción desconocida: ${JSON.stringify(action)}. ` +
        'El motor solo admite el vocabulario declarativo documentado.'
    );
    this.name = 'UnknownActionError';
  }
}

/**
 * Ejecuta una única acción sobre el estado y devuelve sus efectos.
 *
 * Idempotencia: addItem/addEvidence/completeObjective solo producen efecto
 * cuando realmente cambian el estado, de modo que repetir una interacción no
 * duplica evidencias, recompensas ni progreso.
 */
export function executeAction(action: Action, state: GameState): ActionEffect[] {
  switch (action.type) {
    case 'setVariable': {
      state.variables[action.var] = action.value;
      return [{ kind: 'variableSet', var: action.var, value: action.value }];
    }
    case 'incrementVariable': {
      const amount = action.amount ?? 1;
      const current = state.variables[action.var];
      const base = typeof current === 'number' ? current : Number(current) || 0;
      const next = base + amount;
      state.variables[action.var] = next;
      return [{ kind: 'variableSet', var: action.var, value: next }];
    }
    case 'addItem': {
      if (addUnique(state.items, action.item)) {
        return [{ kind: 'itemAdded', item: action.item }];
      }
      return [];
    }
    case 'removeItem': {
      const idx = state.items.indexOf(action.item);
      if (idx >= 0) {
        state.items.splice(idx, 1);
        return [{ kind: 'itemRemoved', item: action.item }];
      }
      return [];
    }
    case 'addEvidence': {
      if (addUnique(state.evidence, action.evidence)) {
        return [{ kind: 'evidenceAdded', evidence: action.evidence }];
      }
      return [];
    }
    case 'completeObjective': {
      if (addUnique(state.completedObjectives, action.objective)) {
        return [{ kind: 'objectiveCompleted', objective: action.objective }];
      }
      return [];
    }
    case 'goToScene': {
      return [{ kind: 'scene', scene: action.scene }];
    }
    case 'startDialogue': {
      return [{ kind: 'dialogue', dialogue: action.dialogue, node: action.node }];
    }
    case 'showMessage': {
      return [{ kind: 'message', text: action.text, tone: action.tone ?? 'info' }];
    }
    case 'finishGame': {
      return [{ kind: 'finish', ending: action.ending }];
    }
    case 'openConclusion': {
      return [{ kind: 'conclusion' }];
    }
    case 'setObjectState': {
      state.objectStates[action.object] = action.state;
      return [{ kind: 'objectStateChanged', object: action.object, state: action.state, sfx: action.sfx }];
    }
    case 'playVideo': {
      return [{
        kind: 'video',
        video: action.video,
        titulo: action.titulo,
        descripcion: action.descripcion,
        modo: action.modo ?? 'modal',
        objeto: action.objeto,
        ancho: action.ancho ?? 0.24,
        poster: action.poster
      }];
    }
    case 'playSequence': {
      return [{ kind: 'sequence', sequence: action.sequence }];
    }
    case 'cameraZoom': {
      return [{
        kind: 'cameraZoom',
        target: action.target,
        scale: action.scale ?? 1.8,
        duration: action.duration ?? 600,
        hold: action.hold ?? 1500
      }];
    }
    case 'cameraPan': {
      return [{
        kind: 'cameraPan',
        to: action.to,
        duration: action.duration ?? 800,
        hold: action.hold ?? 1200
      }];
    }
    case 'startEncounter': {
      return [{ kind: 'encounter', encounter: action.encounter }];
    }
    case 'startMinigame': {
      return [{ kind: 'minigame', minigame: action.minigame }];
    }
    case 'showContent': {
      return [{ kind: 'content', content: action.content }];
    }
    default: {
      throw new UnknownActionError(action);
    }
  }
}

/**
 * Ejecuta una lista de acciones en orden, acumulando efectos.
 * El orden es predecible: los efectos se devuelven en el mismo orden en que
 * las acciones producen cambios.
 */
export function executeActions(actions: Action[] | undefined, state: GameState): ActionEffect[] {
  if (!actions || actions.length === 0) return [];
  const effects: ActionEffect[] = [];
  for (const action of actions) {
    effects.push(...executeAction(action, state));
  }
  return effects;
}
